import type { Level, Opening, OpeningDesign, Vec2, Wall } from '../hp/types';
import { validateLevel } from './edit';

const cache = new WeakMap<Level['walls'], WeakMap<Level['openings'], Map<string, string>>>();

/**
 * A door between two rooms of a project from the app is listed twice, once on each room's copy of
 * their shared wall: two openings of one model on one spot of two coincident walls, the second
 * turned round (the app's `OpenOutside`, `flipped` here). The app draws and picks the plan symbol
 * of the one that is not turned round and nothing for its twin (`OpeningFurnitureController
 * .UpdateDragger`); the plan does the same.
 *
 * Returns, for every opening that stands behind a twin that way, the id of the one shown for both.
 */
export function openingTwins(level: Level): Map<string, string> {
  let byOpenings = cache.get(level.walls);
  if (!byOpenings) { byOpenings = new WeakMap(); cache.set(level.walls, byOpenings); }
  let twins = byOpenings.get(level.openings);
  if (!twins) { twins = findTwins(level); byOpenings.set(level.openings, twins); }
  return twins;
}

interface Placed { opening: Opening; wall: Wall; centre: Vec2 }

function findTwins(level: Level): Map<string, string> {
  const walls = new Map(level.walls.map(wall => [wall.id, wall]));
  const placed: Placed[] = level.openings.flatMap(opening => {
    const wall = walls.get(opening.wallId);
    return wall ? [{ opening, wall, centre: centreOn(wall, opening.offset) }] : [];
  });
  const shownFor = new Map<string, string>();
  for (const [i, first] of placed.entries()) {
    if (shownFor.has(first.opening.id)) continue;
    const group = [first, ...placed.slice(i + 1).filter(other => !shownFor.has(other.opening.id) && twins(first, other))];
    if (group.length < 2) continue;
    const shown = group.find(member => !member.opening.flipped) ?? first;
    for (const member of group) if (member !== shown) shownFor.set(member.opening.id, shown.opening.id);
  }
  return shownFor;
}

/** One model on one spot of two walls lying on one line: the copies an import makes agree to a centimetre or two. */
function twins(a: Placed, b: Placed): boolean {
  const same = a.opening.modelRef.kind === b.opening.modelRef.kind && a.opening.modelRef.id === b.opening.modelRef.id
    && (a.opening.modelRef.kind !== 'none' || (a.opening.kind ?? '') === (b.opening.kind ?? ''));
  if (!same || Math.hypot(a.centre.x - b.centre.x, a.centre.z - b.centre.z) > .05) return false;
  if (Math.abs(a.opening.width - b.opening.width) > .15 || Math.abs(a.opening.sill - b.opening.sill) > .05) return false;
  const across = Math.abs(a.wall.dir.x * b.wall.dir.z - a.wall.dir.z * b.wall.dir.x);
  const apart = Math.abs((b.centre.x - a.wall.start.x) * a.wall.normalIn.x + (b.centre.z - a.wall.start.z) * a.wall.normalIn.z);
  return across < .02 && apart < .05;
}

/** The other copies of the opening `id`: the one shown for it and the copies standing behind it. */
export function openingCopies(level: Level, id: string): string[] {
  const twins = openingTwins(level), shown = twins.get(id) ?? id;
  return [shown, ...[...twins].flatMap(([hidden, of]) => of === shown ? [hidden] : [])].filter(other => other !== id);
}

/**
 * `next`, an edit of `base` that changed or removed the openings `ids`, with their other copies kept in
 * step, as the app keeps the door between two rooms on both rooms' copies of their wall
 * (`OpeningSyncHelper`, `OpeningFactory.CopyProperty`). A copy paired with an edited opening in `base`
 * goes when it goes; otherwise it takes what changed in it (width, height, sill, model, kind, caption,
 * style and angle, materials, and its hand, turned round as before) and stands where it now stands: on
 * its own wall while that holds the spot, else on another copy of the wall that does, its recess
 * mirrored across the wall; over no copy of the wall it goes, as no other room's wall is left to cut.
 * Curtains stay each side's own. Returns `next` itself when no copy had to change.
 */
export function syncOpeningTwins(base: Level, next: Level, ids: Iterable<string>): Level {
  const edited = new Set(ids), follows = new Map<string, { of: string; copy: Opening | null }>();
  for (const id of edited) {
    const before = base.openings.find(opening => opening.id === id);
    if (!before) continue;
    const after = next.openings.find(opening => opening.id === id);
    for (const other of openingCopies(base, id)) {
      if (edited.has(other) || follows.has(other)) continue;
      const twin = base.openings.find(opening => opening.id === other)!;
      follows.set(other, { of: id, copy: after ? follow(base, next, before, after, twin) : null });
    }
  }
  if (!follows.size) return next;
  const placed = new Set<string>();
  let changed = false;
  const openings = next.openings.flatMap(opening => {
    const followed = follows.get(opening.id);
    if (!followed) return [opening];
    placed.add(opening.id);
    if (followed.copy && same(followed.copy, opening)) return [opening];
    changed = true;
    return followed.copy ? [followed.copy] : [];
  });
  // A copy that went earlier in a gesture, while the spot was over no copy of its wall, comes back beside the one it follows.
  for (const [id, followed] of follows) {
    if (placed.has(id) || !followed.copy) continue;
    changed = true;
    openings.splice(openings.findIndex(opening => opening.id === followed.of) + 1, 0, followed.copy);
  }
  return changed ? { ...next, openings } : next;
}

const FOLLOWED = ['width', 'height', 'sill', 'kind', 'caption'] as const;

/** `twin` brought in step with its pair edited from `before` to `after`, or null where no copy of its wall is left under it. */
function follow(base: Level, next: Level, before: Opening, after: Opening, twin: Opening): Opening | null {
  const copy = structuredClone(twin);
  for (const key of FOLLOWED) if (before[key] !== after[key]) assign(copy, key, after[key]);
  if (!same(before.modelRef, after.modelRef)) copy.modelRef = structuredClone(after.modelRef);
  if (!same(before.materials, after.materials)) assign(copy, 'materials', after.materials && structuredClone(after.materials));
  if (before.flipped !== after.flipped) copy.flipped = !twin.flipped;
  const from = next.walls.find(wall => wall.id === after.wallId), own = base.walls.find(wall => wall.id === twin.wallId);
  if (!from || !own) return copy;
  let host = next.walls.find(wall => wall.id === twin.wallId) ?? own;
  if (before.wallId !== after.wallId || before.offset !== after.offset || before.width !== after.width) {
    const centre = centreOn(from, after.offset);
    // On its own wall while that holds the spot, else on another copy of the wall facing the same way.
    const found = wallCopyAt(next, from, centre, Math.sign(dot(own.normalIn, from.normalIn)), own.id);
    if (!found) return null;
    host = found; copy.wallId = found.id; copy.offset = round(along(found, centre));
  }
  if (!same(before.design, after.design) || host.id !== twin.wallId) assign(copy, 'design', mirroredDesign(after.design, from, host, twin.design));
  return copy;
}

/**
 * `level` with the opening `id` given its copy on the other room's copy of its wall, as the app gives a
 * door or window placed between two rooms its mirror (`OpeningSyncHelper.CreateCrossWallMirror`,
 * `OpeningFactory.CreateMirror`): the same model, size, sill, materials and design, turned round, its
 * recess mirrored across the wall, on the wall lying on the same line and facing the other way that holds
 * the opening's centre on its length. The copy goes just before the opening in the list, so an opening
 * just added stays the last. Returns `level` itself where the opening has a copy already, where no copy
 * of its wall holds the spot, or where the copy would not stand there: another opening takes the spot —
 * the other room's own window is never covered (`WallSide.IsSpotTaken`) — or it would not fit that wall.
 */
export function mirrorOpening(level: Level, id: string): Level {
  const opening = level.openings.find(value => value.id === id), from = level.walls.find(wall => wall.id === opening?.wallId);
  if (!opening || !from || openingCopies(level, id).length) return level;
  const centre = centreOn(from, opening.offset), host = wallCopyAt(level, from, centre, -1);
  if (!host) return level;
  const copy: Opening = { ...structuredClone(opening), id: `o_${crypto.randomUUID()}`, wallId: host.id, offset: round(along(host, centre)), flipped: !opening.flipped };
  assign(copy, 'design', mirroredDesign(opening.design, from, host, undefined));
  const index = level.openings.indexOf(opening);
  const next = { ...level, openings: [...level.openings.slice(0, index), copy, ...level.openings.slice(index)] };
  return validateLevel(next, level) ? level : next;
}

/**
 * `next`, an edit of `base` — a gesture as it ends — with a copy (`mirrorOpening`) for each opening it
 * added, moved or resized that has none, as the app ends a drag with `EnsureCrossWallMirror`: a move only
 * takes an existing copy along, so a door that had none stays one-sided until it rests between two rooms.
 */
export function mirrorMovedOpenings(base: Level, next: Level): Level {
  if (next.openings === base.openings) return next;
  const before = new Map(base.openings.map(opening => [opening.id, opening]));
  let level = next;
  for (const opening of next.openings) {
    const was = before.get(opening.id);
    if (!was || PLACEMENT.some(key => was[key] !== opening[key])) level = mirrorOpening(level, opening.id);
  }
  return level;
}

const PLACEMENT = ['wallId', 'offset', 'width', 'height', 'sill'] as const;

/**
 * The copy of `from`'s wall that holds `centre` on its length: a wall lying on the same line and facing
 * the way `from` faces (`facing` 1) or the other way (-1), the wall `prefer` while it does.
 */
function wallCopyAt(level: Level, from: Wall, centre: Vec2, facing: number, prefer?: string): Wall | undefined {
  const holds = (wall: Wall) => wall.id !== from.id && Math.abs(wall.dir.x * from.dir.z - wall.dir.z * from.dir.x) < .02
    && Math.sign(dot(wall.normalIn, from.normalIn)) === facing
    && Math.abs(dot({ x: centre.x - wall.start.x, z: centre.z - wall.start.z }, wall.normalIn)) < .05
    && along(wall, centre) >= -1e-6 && along(wall, centre) <= wall.length + 1e-6;
  const preferred = prefer === undefined ? undefined : level.walls.find(wall => wall.id === prefer);
  return preferred && holds(preferred) ? preferred : level.walls.find(holds);
}

/** `design` of the edited copy as its twin on `host` wears it: its frame in the same plane seen from the other side, its own curtains. */
function mirroredDesign(design: OpeningDesign | undefined, from: Wall, host: Wall, own: OpeningDesign | undefined): OpeningDesign | undefined {
  const curtains = own?.curtains;
  if (!design) return curtains && curtains !== 'none' ? { curtains } : undefined;
  const { curtains: _curtains, recess, ...shared } = design;
  const mirrored: OpeningDesign = { ...shared };
  if (recess !== undefined) {
    // The frame's plane, from the wall's axis toward the room the edited copy faces, measured from the host's side.
    const plane = (from.thickness / 2 - recess) * Math.sign(dot(from.normalIn, host.normalIn));
    mirrored.recess = round(Math.min(host.thickness, Math.max(0, host.thickness / 2 - plane)));
  }
  if (curtains !== undefined) mirrored.curtains = curtains;
  return mirrored;
}

function assign<K extends keyof Opening>(opening: Opening, key: K, value: Opening[K]): void {
  if (value === undefined) delete opening[key]; else opening[key] = value;
}
const along = (wall: Wall, p: Vec2) => (p.x - wall.start.x) * wall.dir.x + (p.z - wall.start.z) * wall.dir.z;
const centreOn = (wall: Wall, offset: number): Vec2 => ({ x: wall.start.x + wall.dir.x * offset, z: wall.start.z + wall.dir.z * offset });
const dot = (a: Vec2, b: Vec2) => a.x * b.x + a.z * b.z;
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const round = (value: number) => Math.round(value * 1e4) / 1e4;
